import { css } from '@/styled-system/css';
import { cva } from '@/styled-system/css';

/**
 * FullscreenOverlay Backdrop - semi-transparent overlay
 */
export const backdrop = css({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100vw',
  height: '100vh',
  '@supports (height: 100dvh)': {
    height: '100dvh',
  },
  bg: 'hpModalOverlay',
  zIndex: 'zIndexOverLoader',
  md: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

/**
 * FullscreenOverlay Content - main container
 */
export const content = cva({
  base: {
    position: 'relative',
    width: '100vw',
    height: '100vh',
    '@supports (height: 100dvh)': {
      height: '100dvh',
    },
    bg: 'hpxWhite',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    md: {
      width: 'auto',
      height: 'auto',
      maxWidth: '900px',
      maxHeight: '90vh',
      borderRadius: '12px',
    },
  },
  variants: {
    variant: {
      standard: {
        // Standard variant has normal header + body + footer layout
      },
      media: {
        md: {
          width: '100vw',
          height: '100vh',
          maxWidth: 'none',
          maxHeight: 'none',
          borderRadius: 0,
        },
      },
    },
  },
});

/**
 * FullscreenOverlay Header - varies by variant and layout
 */
export const header = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '48px',
    px: '1x',
    py: '1x',
    zIndex: 'zIndexParent',
  },
  variants: {
    layout: {
      sticky: {
        position: 'sticky',
        top: 0,
        bg: 'hpxWhite',
      },
      floating: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bg: 'transparent',
        zIndex: 'zIndexOverParent',
      },
    },
    variant: {
      standard: {},
      media: {},
    },
  },
});

/**
 * Header column layout - three column grid
 */
export const headerColumns = css({
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  width: '100%',
  gap: '1x',
});

export const headerColumn1 = css({
  justifySelf: 'start',
});

export const headerColumn2 = css({
  justifySelf: 'center',
});

export const headerColumn3 = css({
  justifySelf: 'end',
  display: 'flex',
  gap: '1x',
  alignItems: 'center',
});

export const headerTitle = css({
  fontSize: 'lg',
  fontWeight: 'bold',
  color: 'hpxBlue600',
  textAlign: 'center',
});

/**
 * FullscreenOverlay Body - main content area
 */
export const body = cva({
  base: {
    flex: 1,
    overflow: 'auto',
    position: 'relative',
  },
  variants: {
    padding: {
      standard: {
        px: '2x',
        py: '2x',
      },
      'edge-to-edge': {
        p: 0,
        display: 'flex',
        flexDirection: 'column',
      },
    },
    variant: {
      standard: {},
      media: {
        p: 0,
        display: 'flex',
        flexDirection: 'column',
      },
    },
  },
});

/**
 * FullscreenOverlay Footer - action buttons
 */
export const footer = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '72px',
    px: '2x',
    py: '2x',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: 'hpxGrey400',
    bg: 'hpxWhite',
  },
  variants: {
    variant: {
      buttons: {
        justifyContent: 'flex-end',
        gap: '1x',
      },
      'equal-width': {
        gap: '1x',
        '& > *': {
          flex: 1,
        },
      },
      text: {
        fontSize: 'sm',
        color: 'hpxGrey600',
      },
    },
    overlayVariant: {
      standard: {},
      media: {
        // Media variant has no footer
        display: 'none',
      },
    },
  },
});

/**
 * Media variant specific styles
 */
export const mediaContainer = css({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bg: 'hpxBlack',
});

export const mediaContent = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

export const mediaOverlayInfo = css({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  bg: 'linear-gradient(transparent, rgba(0, 0, 0, 0.8))',
  color: 'hpxWhite',
  p: '3x',
  zIndex: 'zIndexParent',
});

export const floatingActions = cva({
  base: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'column',
    gap: '2x',
    zIndex: 'zIndexOverParent',
  },
  variants: {
    position: {
      left: {
        left: '2x',
      },
      right: {
        right: '2x',
      },
    },
  },
});

/**
 * Close button styling
 */
export const closeButton = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  bg: 'transparent',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'hpxGrey400',
  color: 'hpxBlue600',
  cursor: 'pointer',
  transitionProperty: 'all',
  transitionDuration: '0.2s',

  '&:hover': {
    bg: 'hpxGrey100',
    borderColor: 'hpxGrey500',
  },

  '&:focus': {
    outlineWidth: '2px',
    outlineStyle: 'solid',
    outlineColor: 'hpxTeal500',
    outlineOffset: '2px',
  },
});

/**
 * Floating close button for media variant
 */
export const floatingCloseButton = css({
  position: 'absolute',
  top: '2x',
  left: '2x',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  bg: 'rgba(255, 255, 255, 0.9)',
  border: 'none',
  color: 'hpxBlue600',
  cursor: 'pointer',
  transitionProperty: 'all',
  transitionDuration: '0.2s',
  zIndex: 'zIndexOverParent',

  '&:hover': {
    bg: 'rgba(255, 255, 255, 1)',
    transform: 'scale(1.05)',
  },

  '&:focus': {
    outlineWidth: '2px',
    outlineStyle: 'solid',
    outlineColor: 'hpxTeal500',
    outlineOffset: '2px',
  },
});

/**
 * Visual treatment for close button when used inline in a floating media header (with actions).
 * Same look as floatingCloseButton but without absolute positioning.
 */
export const mediaCloseButtonInline = css({
  bg: 'rgba(255, 255, 255, 0.9)',
  border: 'none',

  '&:hover': {
    bg: 'rgba(255, 255, 255, 1)',
    transform: 'scale(1.05)',
  },
});
